import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import axios from "axios";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
import { useStripe, useElements, PaymentElement, AddressElement } from "@stripe/react-stripe-js";
import { StripePaymentElementOptions, StripeAddressElementOptions } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { stripeCountryCodes } from "../../../constants/Currencies";
import { InfoCard } from "../../common/Payment/InfoCard";
import styles from "./ChangePlan.module.scss";

type Props = {
  billingInfo: {
    company_name: string;
    workspace_name: string;
    address: {
      postal_code: number;
      prefecture: string;
      city: string;
      street: string;
      building: string;
    };
    billing_email: string;
  };
  countryCode: string;
  client: string;
  updatePlanForOverseas: () => Promise<void>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  card: any;
  currentPlan: any;
  plan_code: string;
  afterPlanCode: string;
  currency: any;
  period_type: string;
};

type SearchParams = {
  code: string;
  period_type: string;
  payment_method: string;
  currency?: string;
};

const CheckoutForm = forwardRef(
  (
    {
      billingInfo,
      countryCode,
      client,
      updatePlanForOverseas,
      setIsLoading,
      card,
      currentPlan,
      plan_code,
      afterPlanCode,
      currency,
      period_type,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState("");
    const [displayFormFlg, setDisplayFormFlg] = useState(true);

    const { address, company_name } = billingInfo || {};

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    let params: SearchParams;
    if (afterPlanCode === "global_free") {
      params = {
        code: afterPlanCode,
        period_type: "month",
        payment_method: "credit_card",
        currency: "jpy",
      };
    } else if (plan_code === "global_free") {
      params = {
        code: afterPlanCode,
        period_type: period_type,
        payment_method: "credit_card",
        currency: currency.code.toLocaleLowerCase(),
      };
    } else {
      params = {
        code: afterPlanCode,
        period_type: period_type,
        payment_method: "credit_card",
      };
    }

    const updateCard = async (token: string) => {
      await axios
        .post(`/${client}/api_cards/change_default`, {
          token_id: token,
        })
        .then(() => {
          return;
        })
        .catch((_) => {
          return;
        });
    };

    const handleSubmit = async () => {
      setErrorMessage("");

      if (displayFormFlg) {
        // Stripe.jsがまだロードされていない場合
        if (!stripe || !elements) return;

        const addressElement = elements.getElement("address");

        if (!address) {
          return;
        }

        const { value: addressValue, complete: isAddressComplete } = (await addressElement?.getValue()) || {};

        // 請求先住所の入力が完了していない場合
        if (!isAddressComplete) return;

        setIsLoading(true);
        const redirectUrl = new URL(
          `${window.location.origin}/${client}/api_cards/request_three_d_secure_result_for_global`,
        );
        redirectUrl.searchParams.set("code", params.code ?? "");
        redirectUrl.searchParams.set("period_type", params.period_type ?? "");
        redirectUrl.searchParams.set("payment_method", params.payment_method ?? "");
        redirectUrl.searchParams.set("currency", params.currency ?? "");

        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: redirectUrl.toString(),
            payment_method_data: {
              billing_details: addressValue,
            },
          },
          redirect: "if_required",
        });

        if (result.error) {
          setErrorMessage(result.error.message || t("CheckoutForm.カード情報の登録に失敗しました"));
          setIsLoading(false);
          return false;
        }

        if (result.setupIntent && result.setupIntent.payment_method) {
          try {
            await updateCard(result.setupIntent.payment_method as string);
            await updatePlanForOverseas();
          } catch (error) {
            setIsLoading(false);
            setErrorMessage(t("CheckoutForm.カード情報の更新またはプラン更新に失敗しました"));
          }
        }
      } else {
        setIsLoading(true);
        try {
          await updatePlanForOverseas();
        } catch (error) {
          setIsLoading(false);
          setErrorMessage(t("CheckoutForm.カード情報の更新またはプラン更新に失敗しました"));
        }
      }
    };

    const paymentElementOptions: StripePaymentElementOptions = {
      fields: { billingDetails: { address: "never" } },
      paymentMethodOrder: ["card"],
      wallets: {
        applePay: "never",
        googlePay: "never",
      },
    };

    const addressElementOptions: StripeAddressElementOptions = {
      mode: "billing",
      allowedCountries: stripeCountryCodes,
      defaultValues: {
        name: company_name || "",
        address: {
          country: countryCode,
          postal_code: address?.postal_code?.toString() || "",
          state: address?.prefecture || "",
          city: address?.city || "",
          line1: address?.street || "",
          line2: address?.building || "",
        },
      },
    };

    useEffect(() => {
      if (card) {
        setDisplayFormFlg(false);
      }
    }, [card]);

    return (
      <>
        {errorMessage && <div className={styles.updatePlanErr}>{errorMessage}</div>}
        {displayFormFlg ? (
          <>
            <div style={{ marginBottom: 40 }}>
              <PaymentElement options={paymentElementOptions} />
            </div>
            <div style={{ marginBottom: 40 }}>
              <AddressElement options={addressElementOptions} />
            </div>
          </>
        ) : (
          <div style={{ marginBottom: 40 }}>
            <InfoCard
              card={card}
              paymentMethod="card"
              currentPlan={currentPlan}
              changeFunc={() => setDisplayFormFlg(true)}
            />
            {errorMessage && <div className={styles.updatePlanErr}>{errorMessage}</div>}
          </div>
        )}
      </>
    );
  },
);

export default CheckoutForm;
